import {FormControl, DialogContent, Divider, useTheme} from "@mui/material";
import {Grid} from "@mui/system";
import CustomModal from "components/CustomModal";
import {DefaultModalFooter} from "components/CustomModal/components";
import pxToRem from "hooks/usePxToRem";
import {createRef, useRef, useState} from "react";
import {CustomButton} from "ui";
import ConfirmModal from "components/ConfirmModal";
import {
  EmailFields,
  EmailPreview,
} from "components/ModalCompleteActivity/components";
import {splitAndRemoveSpacesFromString} from "util/splitAndRemoveSpacesFromString";
import {EmailFilesPreview} from "components";
import useEmailFilesPreview from "components/EmailFilesPreview/useEmailFilesPreview";
import {getFileExtensionFromFileName} from "util/getFileExtensionFromFileName";

export type EmailData = {
  subject: string;
  to: string[];
  cc: string[];
  bcc: string[];
  attachment: {name: string; url: string; id: string; guid: string}[];
};

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  processId: number;
  processNumber?: string;
  typeOfEmail?: string;
  modalIcon?: string;
  handleSendEmail: (
    formRef: React.RefObject<HTMLFormElement>,
    emailData: EmailData,
    editedEmail: string,
  ) => void;
  emailData: EmailData;
  setEmailData: React.Dispatch<React.SetStateAction<EmailData>>;
  emailReturn: string;
  formRef: React.RefObject<HTMLFormElement>;
  handleAbortFollowUp?: () => void;
  disableContent?: boolean;
  customTitle?: string;
  customSubtitle?: string;
};

const EmailPreviewModal = (props: Props) => {
  const {
    isOpen,
    handleClose,
    processNumber = "",
    typeOfEmail = "follow up",
    modalIcon = "outgoing_mail",
    handleSendEmail,
    emailData,
    emailReturn,
    formRef,
    handleAbortFollowUp = handleClose,
    processId,
    disableContent = false,
    customTitle,
    customSubtitle,
  } = props;

  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);
  const [idAttachToRemove, setIdAttachToRemove] = useState("");
  const [localEmailData, setLocalEmailData] = useState(emailData);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const isSendingEmailRef = useRef(false);

  const editorRef = createRef<HTMLDivElement>();

  const handleConfirmRemoveAttach = (id: string) => {
    setConfirmModalIsOpen(true);
    setIdAttachToRemove(id);
  };

  const handleDeleteAttachment = (id: string) => {
    const newEmailData: EmailData = {
      ...localEmailData,
      attachment: localEmailData.attachment.filter((item) => item.id !== id),
    };

    setLocalEmailData(newEmailData);
    setConfirmModalIsOpen(false);
  };

  const theme = useTheme();

  const handleAutoComplete = (_: unknown, nv, field: string) => {
    const newValues = splitAndRemoveSpacesFromString(nv[nv.length - 1]) ?? [];
    const fullArray = [...nv.slice(0, -1), ...newValues];

    setLocalEmailData((prev) => ({...prev, [field]: fullArray}));
  };

  const handleChangeSubject = (_: unknown, nv, field: string) => {
    setLocalEmailData((prev) => ({...prev, [field]: nv}));
  };

  const handleSend = async () => {
    if (isSendingEmailRef.current) {
      return;
    }

    isSendingEmailRef.current = true;
    setIsSendingEmail(true);

    try {
      await handleSendEmail(
        formRef,
        localEmailData,
        editorRef.current?.innerHTML ?? "",
      );
    } finally {
      isSendingEmailRef.current = false;
      setIsSendingEmail(false);
    }
  };

  const {fileUrl, setFileUrl, fileName, fileId, fileGuid, handleClickFileChip} =
    useEmailFilesPreview();

  return (
    <>
      <ConfirmModal
        isOpen={confirmModalIsOpen}
        title="Remover anexo"
        subtitle="Essa ação não pode ser desfeita!"
        confirmButtonFn={() => handleDeleteAttachment(idAttachToRemove)}
        onClose={() => setConfirmModalIsOpen(false)}
      />
      {!!fileUrl && (
        <EmailFilesPreview
          fileExtension={getFileExtensionFromFileName(fileName)}
          fileUrl={fileUrl}
          setFileUrl={setFileUrl}
          fileName={fileName}
          fileId={fileId}
          fileGuid={fileGuid}
        />
      )}

      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        title={customTitle ?? `Enviar ${typeOfEmail}`}
        titleIcon={modalIcon}
        subtitle={
          customSubtitle ??
          `Encaminhar ${typeOfEmail} para o processo ${processNumber}`
        }
        maxWidth="lg"
      >
        <FormControl
          component="form"
          onSubmit={() => {
            handleSend();
          }}
          id="mainForm"
          ref={formRef}
        >
          <Grid container size={12}>
            <Grid size={4.25}>
              <DialogContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: pxToRem(12),
                  paddingTop: pxToRem(7),
                  paddingRight: pxToRem(0),
                  maxHeight: pxToRem(400),
                }}
              >
                <Grid
                  container
                  rowGap={pxToRem(8)}
                  sx={{
                    maxHeight: pxToRem(400),
                    overflowY: "scroll",
                    padding: `0 ${pxToRem(10)} 0 ${pxToRem(5)}`,
                  }}
                >
                  <EmailFields
                    emailData={localEmailData}
                    setEmailData={setLocalEmailData}
                    processId={processId}
                    handleChangeSubject={handleChangeSubject}
                    handleAutoComplete={handleAutoComplete}
                    handleClickFileChip={handleClickFileChip}
                    handleConfirmRemoveAttach={handleConfirmRemoveAttach}
                    disableContent={disableContent}
                  />
                </Grid>
              </DialogContent>
            </Grid>
            <Grid
              size={0.2}
              display="flex"
              justifyContent="center"
              p={`${pxToRem(10)} 0`}
            >
              <Divider orientation="vertical" />
            </Grid>
            <Grid
              size="grow"
              sx={{
                height: pxToRem(400),
                overflowY: "scroll",
                display: "unset",
              }}
            >
              <EmailPreview
                emailContent={emailReturn}
                editorRef={editorRef}
                disabled={disableContent}
              />
            </Grid>
            <DefaultModalFooter>
              <Grid
                container
                size={12}
                gap={pxToRem(8)}
                justifyContent="flex-end"
              >
                <Grid>
                  <CustomButton
                    variant="outlined"
                    onClickFn={handleAbortFollowUp}
                  >
                    {"Cancelar"}
                  </CustomButton>
                </Grid>
                <Grid>
                  <CustomButton
                    onClickFn={handleSend}
                    disabled={disableContent || isSendingEmail}
                  >
                    Enviar Email
                  </CustomButton>
                </Grid>
              </Grid>
            </DefaultModalFooter>
          </Grid>
        </FormControl>
      </CustomModal>
    </>
  );
};

export default EmailPreviewModal;
